import { ColumnDef } from '@tanstack/react-table';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Divider, Text } from 'theme-ui';
import { getAllClients } from '../../../store/actions/client.action';
import DataTable from '../../Tabels/Tabel';
import { TabelHeadingTxt } from '../../Text/TabelCommonTxt';
import plus from "../../assets/images/Icon/plus.svg";
import TableActions from '../users/TableActions';
import queryString from 'query-string';
import { getApiEndPoint } from '../../../utils/GenerateQueryString';
import debounce from 'lodash/debounce';
import { IClientData, RootState } from '../../../../@types';

const ClientsTable: React.FC = () => {
    const { allClients, isLoading } = useSelector((state: RootState) => state.client);
    const { paginations } = useSelector((state: RootState) => state.pagination);
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams({
        currentPage: "1",
        limit: "15",
    });
    const limit = parseInt(searchParams.get('limit') || "15");
    const currentPage = parseInt(searchParams.get('currentPage') || "1");
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [refreshCount, setRefreshCount] = useState<number>(0);
    const debouncedFetch = useMemo(() => debounce((apiEndPoint: string) => {
        dispatch(getAllClients(apiEndPoint));
    }, 100), [dispatch]);

    useEffect(() => {
        let apiEndPoint = getApiEndPoint({ limit, currentPage }, location.search, columns?.map((val: ColumnDef<IClientData> & { accessorKey?: string, filterType?: string }) => ({ accessorKey: val.accessorKey || "", filterType: val.filterType || "" })) || [])
        debouncedFetch(apiEndPoint);

        return () => {
            debouncedFetch.cancel();
        };
    }, [refreshCount, location?.search, limit, currentPage, debouncedFetch]);

    const handleclick = () => {
        navigate("/create-client");
    }

    const columns = useMemo<ColumnDef<IClientData>[]>(() => [
        {
            header: 'No',
            enableColumnFilter: false,
            accessorKey: "no",
            cell: ({ row }) => {
                return (
                    <Text variant='Poppins14Normal24lh'>{((currentPage - 1) * limit) + row?.index + 1}</Text>
                )
            },
        },
        {
            accessorKey: "name",
            header: 'Name',
            cell: info => info.getValue(),
            filterType: "textbox",
        },
        {
            header: 'Email',
            accessorKey: "email",
            cell: info => info.getValue(),
            filterType: "textbox",
        },
        {
            header: 'Accessor',
            accessorKey: "Accessor",
            enableColumnFilter: false,
            cell: ({ row }) => {
                return (
                    <TableActions row={row} editTo={`/edit-client/${row?.original?._id}`} deleteMessage='Are you sure you want to delete this client?' deleteApi={`clients/delete-client/${row?.original?._id}`} toggleRefresh={() => setRefreshCount(prev => prev + 1)} />
                )
            },
        }
    ], [allClients?.data]);

    return (
        <div className='main-content'>
            <TabelHeadingTxt Title='Clients' Count={allClients?.count || 0} BtnTxt='Create Client' src={plus} onClick={handleclick} sx={{ backgroundColor: '#7743DB', color: 'white' }} url={'/client'} ml={'60px'} />
            <Divider sx={{ color: '#D9D7DD' }} opacity={2} my={[3, 3, 4]} />
            <DataTable
                loader={isLoading}
                columns={columns}
                data={allClients?.data || []}
                TotalLength={allClients?.count || 15}
                perPageCount={limit || 15}
                currentPage={paginations?.currentPage || currentPage}
                onPerPageChange={(itemsPerPage) => {
                    if (searchParams?.size > 2) {
                        const parsedQuery = queryString.parse(location.search);
                        setSearchParams({
                            ...parsedQuery,
                            limit: itemsPerPage.toString(),
                        });
                    } else if ((allClients?.count || 0) < Number(itemsPerPage)) {
                        setSearchParams({
                            currentPage: '1',
                            limit: itemsPerPage.toString(),
                        });
                    }
                    else {
                        setSearchParams({
                            currentPage: currentPage.toString(),
                            limit: itemsPerPage.toString(),
                        });
                    }
                }}
                onPageChange={(value: string) => {
                    if (searchParams?.size > 2) {
                        const parsedQuery = queryString.parse(location.search);
                        setSearchParams({
                            ...parsedQuery,
                            currentPage: value.toString(),
                        });
                    } else {
                        setSearchParams({
                            currentPage: value.toString(),
                            limit: limit.toString(),
                        });
                    }
                }}
            />
        </div>
    )
};

export default ClientsTable;
